import React, { useState, useEffect } from 'react';
import Contact from '../components/Contact'; // Import the Contact component

// Navigation Component for About Page - Matching Main Page
const AboutNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 sm:h-20 items-center">
          <div className="flex-shrink-0 flex items-center">
            <a href="/">
              <img 
                src="/main-logo.png" 
                alt="Banner Day Threads" 
                className="h-12 sm:h-16 opacity-90 hover:opacity-100 transition duration-300 drop-shadow-lg" 
              />
            </a>
          </div>
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-6 lg:space-x-8">
              <a href="/" className="text-black hover:text-gray-700 px-2 lg:px-3 py-2 font-medium drop-shadow-lg text-sm lg:text-base">Home</a>
              <a href="/styles" className="text-black hover:text-gray-700 px-2 lg:px-3 py-2 font-medium drop-shadow-lg text-sm lg:text-base">Our Styles</a>
              <a href="/about" className="text-black hover:text-gray-700 px-2 lg:px-3 py-2 font-medium drop-shadow-lg text-sm lg:text-base">About</a>
              <a href="/contact-us" className="bg-blue-600 text-white px-4 lg:px-6 py-2 rounded-full font-medium hover:bg-blue-700 transition duration-300 shadow-lg text-sm lg:text-base">Get Started</a>
            </div>
          </div>
          <div className="md:hidden flex items-center space-x-3">
            <a 
              href="/contact-us" 
              className="bg-blue-600 text-white px-4 py-2 rounded-full font-medium hover:bg-blue-700 transition duration-300 shadow-lg text-sm"
            >
              Get Started
            </a>
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className="text-black hover:text-gray-700 focus:outline-none drop-shadow-lg"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white bg-opacity-95 rounded-lg backdrop-blur-sm shadow-lg">
              <a href="/" className="block text-black hover:text-gray-700 px-3 py-3 font-medium text-base">Home</a>
              <a href="/styles" className="block text-black hover:text-gray-700 px-3 py-3 font-medium text-base">Our Styles</a>
              <a href="/about" className="block text-black hover:text-gray-700 px-3 py-3 font-medium text-base">About</a>
              <a href="/contact-us" className="block bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition duration-300 text-center mx-3 mt-2">Get Started</a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// FadeIn Animation Component
const FadeIn = ({ children, className = "", delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [delay]);

  return (
    <div 
      ref={ref} 
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
};

// Team Member Card Component
const TeamMemberCard = ({ name, bio, delay = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Generate a placeholder avatar with initials and gradient background
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('');
  };
  
  const gradients = [
    'from-blue-700 to-blue-600',
    'from-blue-500 to-purple-500', 
    'from-purple-400 to-yellow-500',
    'from-yellow-500 to-yellow-400'
  ];
  
  const gradient = gradients[name.length % gradients.length];

  return (
    <FadeIn delay={delay}>
      <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2">
        {/* Avatar and Name Section */}
        <div className="p-8 text-center border-b border-gray-100">
          <div className={`w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
            <span className="text-white font-bold text-2xl">
              {getInitials(name)}
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{name}</h3>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300"
          >
            {isExpanded ? 'Show Less' : 'Read Bio'}
            <svg 
              className={`ml-2 w-5 h-5 transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        {/* Bio Section with Smooth Expand/Collapse */}
        <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-8">
            <p className="text-gray-700 leading-relaxed">
              {bio}
            </p>
          </div>
        </div>
      </div>
    </FadeIn>
  );
};

// Footer Section - Matching Main Page
const Footer = () => (
  <footer className="bg-gray-900 text-white pt-12 sm:pt-16 pb-6 sm:pb-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
        <div className="sm:col-span-2 lg:col-span-1">
          <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4 sm:mb-6">
            Banner Day Threads
          </h3>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
            We craft premium custom headwear that fully highlights your brand. Not ours.
          </p>
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Products</h4>
          <ul className="space-y-1 sm:space-y-2">
            {['Snapbacks', 'Rope Hats', 'Trucker Hats', 'Bucket Hats', 'Visors'].map((item, index) => (
              <li key={index}>
                <span className="text-gray-400 text-sm sm:text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Company</h4>
          <ul className="space-y-1 sm:space-y-2">
            <li>
              <a href="/about" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                About
              </a>
            </li>
            <li>
              <a href="/contact-us" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                Contact Us
              </a>
            </li>
          </ul>
        </div>
        {/* <div>
          <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Legal</h4>
          <ul className="space-y-1 sm:space-y-2">
            <li>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                Terms of Service
              </a>
            </li>
            <li>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                Privacy Policy
              </a>
            </li>
          </ul>
        </div> */}
      </div>
      <div className="border-t border-gray-800 mt-8 sm:mt-12 pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center">
        <p className="text-gray-400 mb-4 md:mb-0 text-sm sm:text-base text-center md:text-left">
        © 2025 Banner Day Threads, LLC. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// About Page Component
const AboutPage = () => {
  const teamMembers = [
    {
      name: "Scott Shuler",
      bio: "Scott began his merchandising career with the NBA's Utah Jazz in 1990. He later joined Top of the World in 2000, serving as President until 2024 and building it into a collegiate headwear leader with partnerships at more than 300 universities. Under his leadership, Top of the World secured exclusive deals with the University of Florida and Oklahoma, expanded into mass-market retail, and launched corporate and private-label programs with brands like Coca-Cola, Bridgestone, Hard Rock, and Black Clover."
    },
    {
      name: "Quayla Caldwell", 
      bio: "Quayla launched her headwear design career at Top of the World in 1998, where she led design, graphics, and product development for 20 years. After Top of the World's acquisition by Fanatics, she oversaw global headwear merchandising, factory operations, and design innovation. She is known for her expertise in logo creation, branding, and bringing fresh headwear concepts to market, collaborating with collegiate and apparel partners to deliver trend-forward products."
    },
    {
      name: "Eric Barnhart",
      bio: "Eric began his collegiate marketing career in 1989 with Host Communications, where he spent 12 years in publishing, media, and university partnerships. In 2001, he joined Learfield as GM of Badger Sports Properties, later moving to the University of Oklahoma in 2003 to lead Sooner Sports Properties. With more than 30 years of experience in partnerships, sponsorships, and marketing activations, Eric has built lasting relationships with both corporations and universities across the country."
    },
    {
      name: "Caleb Fox",
      bio: "Caleb's digital marketing career began at Southwest Airlines in 2008, followed by roles with the Dallas Stars and PepsiCo. In 2016, he became one of the first employees at Simple Modern, where he built its direct-to-consumer eCommerce business, helped secure licensing with 150+ universities as well as the NBA, NFL, and Disney, and expanded into mass retail with Target and Walmart. He brings deep expertise in digital strategy, business analysis, and scalable growth."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AboutNavigation />
      
      {/* Hero Image Section */}
      <section className="relative">
        <div className="w-full h-96 md:h-[500px] overflow-hidden">
          <img 
            src="/about.png" 
            alt="Banner Day Threads - About Our Custom Headwear Company"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <FadeIn>
              <h1 className="text-4xl md:text-6xl font-bold text-white text-center drop-shadow-lg">
                About{' '}
                <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent drop-shadow-lg">
                  Banner Day Threads
                </span>
              </h1>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Opening Paragraph */}
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xl text-blue-600 leading-relaxed max-w-4xl mx-auto">
                At Banner Day Threads, our story is built on nearly 30 years of headwear expertise and a legacy of innovation.
                Our founders and design team bring decades of hands-on experience in the industry,
                having mastered the art and science of crafting premium headwear.
              </p>
            </div>
          </FadeIn>
          
          {/* Main Header */}
          <FadeIn>
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Every Day is a Banner Day
              </h2>
            </div>
          </FadeIn>
          
          {/* Main Content */}
          <div className="space-y-8">
            <FadeIn>
              <p className="text-gray-700 leading-relaxed text-lg">
                We believe every day should feel like a Banner Day - relaxed, vibrant, and full of possibility.
                Whether you're representing a corporate brand or launching a retail empire,
                our mission is to create custom hats that put your brand in the spotlight, not ours.
                With a team that understands the nuances of headwear, corporate sponsorships, and cutting-edge marketing,
                we've revolutionized the process of designing premium custom hats.
              </p>
            </FadeIn>
            
            <FadeIn>
              <p className="text-gray-700 leading-relaxed text-lg">
                After years working behind the scenes for the biggest names in the industry,
                we identified an opportunity to streamline and modernize the art of headwear creation - making it more accessible, seamless, and fast.
              </p>
            </FadeIn>
            
            <FadeIn>
              <p className="text-gray-700 leading-relaxed text-lg">
                With low minimums, rapid turnarounds, and an unwavering commitment to quality craftsmanship,
                we redefine what's possible in custom headwear. Every hat is a bespoke reflection of your brand,
                crafted with premium materials, precise detailing, and the highest level of customization.
                Let's create something extraordinary together - get the best custom hats to showcase your brand,
                and then sit back, relax, and enjoy a Banner Day...every day of the week.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Meet the{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Team
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Decades of combined expertise in headwear design, corporate partnerships, and brand development.
              </p>
            </div>
          </FadeIn>

          {/* Team Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <TeamMemberCard 
                key={index}
                name={member.name}
                bio={member.bio}
                delay={index * 200}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Add Contact Section */}
      <Contact />

      <Footer />
    </div>
  );
};

export default AboutPage;